import { Component } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Select, Store } from '@ngxs/store';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { ButtonModule, NumberfieldModule, TextfieldModule } from '@ed---interne/ng-uui-components';
import { BillingMilestoneModel } from '../../../project/project.models';
import { CommonModule } from '@angular/common';
import { SharedModule } from 'src/app/shared/shared.module';
import { Router, RouterModule } from '@angular/router';
import { HeaderService } from 'src/app/shared/services/header.service';
import { combineLatest, Observable } from 'rxjs';
import {
    DisplayedColumns,
    DisplayType,
} from 'src/app/shared/components/ed-table/ed-table.component';
import { MatTableDataSource } from '@angular/material/table';
import {
    DashboardState,
    GetBillingMilestones,
    GetTimeEntriesByActivities,
    GetTimeEntriesByTypologies,
} from '../../dashboard.state';
import { ApexAxisChartSeries, ApexDataLabels, ApexXAxis, ApexYAxis } from 'ng-apexcharts';
import { convertNumber } from 'src/app/shared/all.helpers';
import { AggregatedEntriesByMonth } from '../../dashboard.model';

@UntilDestroy()
@Component({
    selector: 'dashboard-timesheet',
    templateUrl: './dashboard-timesheet.component.html',
    styleUrls: ['./dashboard-timesheet.component.scss'],
    standalone: true,
    schemas: [],
    imports: [
        CommonModule,
        ReactiveFormsModule,
        FormsModule,
        SharedModule,
        TextfieldModule,
        ButtonModule,
        NumberfieldModule,
        RouterModule,
    ],
})
export class DashboardTimesheetComponent {
    public now: Date = new Date();

    @Select(DashboardState.year)
    public year$!: Observable<number>;
    public year: string = String(this.now.getFullYear());
    public yearsOptions: { value: string; label: string }[] = Array.from(Array(5).keys()).map(
        (n) => ({ value: String(Number(this.year) - n), label: String(Number(this.year) - n) }),
    );

    @Select(DashboardState.timeEntriesByActivities)
    public timeEntriesByActivities$!: Observable<AggregatedEntriesByMonth[]>;
    public timeEntriesByActivities: AggregatedEntriesByMonth[] = [];
    private activitiesColumnsBase: DisplayedColumns[] = [
        {
            displayType: DisplayType.TEXT,
            objectDisplayName: 'Activité',
            objectKey: 'label',
            bold: true,
        },
        {
            displayType: DisplayType.NUMBER,
            objectDisplayName: 'Total',
            objectKey: 'total',
            bold: false,
        },
    ];
    public activitiesColumns: DisplayedColumns[] = [];
    public activitiesData = new MatTableDataSource<{}>([]);

    @Select(DashboardState.timeEntriesByTypologies)
    public timeEntriesByTypologies$!: Observable<AggregatedEntriesByMonth[]>;
    public timeEntriesByTypologies: AggregatedEntriesByMonth[] = [];
    public typologiesSeries: ApexAxisChartSeries = [];
    public typologiesDataLabels: ApexDataLabels = { enabled: true, enabledOnSeries: [0, 1, 2, 3] };
    public typologiesXaxisCategories: ApexXAxis['categories'] = [];
    public typologiesYaxis: ApexYAxis[] = [
        {
            seriesName: ['Production', 'Commercial', 'Interne', 'Congés'],
            title: {
                text: 'Jours',
            },
            labels: {
                formatter: (value: number) => convertNumber(value, 1),
            },
        },
    ];

    @Select(DashboardState.isLoading)
    public isLoading$!: Observable<boolean>;
    public isLoading: boolean = true;

    constructor(
        private readonly router: Router,
        private readonly headerService: HeaderService,
        private readonly store: Store,
    ) {
        this.isLoading$.pipe(untilDestroyed(this)).subscribe((isLoading: boolean) => {
            this.isLoading = isLoading;
        });
        this.year$.pipe(untilDestroyed(this)).subscribe((year: number) => {
            this.year = String(year);
        });

        combineLatest([this.timeEntriesByActivities$, this.year$])
            .pipe(untilDestroyed(this))
            .subscribe(([timeEntriesByActivities, year]) => {
                this.timeEntriesByActivities = timeEntriesByActivities;

                this.activitiesColumns = [
                    ...this.activitiesColumnsBase,
                    ...Array.from(Array(12).keys()).map((e, idx) => ({
                        displayType: DisplayType.NUMBER,
                        objectDisplayName: `${year}-${(idx + 1).toString().padStart(2, '0')}`,
                        objectKey: idx.toString(),
                    })),
                ];
                this.activitiesData = new MatTableDataSource<{}>(
                    timeEntriesByActivities.map((t) => {
                        const line: any = {
                            label: t.label,
                            total: t.months.reduce((tot, m) => tot + m, 0),
                        };
                        t.months.map((m, idx) => {
                            line[idx.toString()] = m;
                        });
                        return line;
                    }),
                );
            });

        combineLatest([this.timeEntriesByTypologies$, this.year$])
            .pipe(untilDestroyed(this))
            .subscribe(([timeEntriesByTypologies, year]) => {
                this.timeEntriesByTypologies = timeEntriesByTypologies;
                this.typologiesXaxisCategories = Array.from(Array(12).keys()).map(
                    (e, idx) => `${year}-${(idx + 1).toString().padStart(2, '0')}-01`,
                );
                this.typologiesSeries = this.timeEntriesByTypologies.map((t) => ({
                    name: t.label,
                    type: 'column',
                    data: t.months,
                }));
                this.typologiesYaxis[0].seriesName =
                    this.typologiesSeries.map((t) => t.name || '') || [];
            });
    }

    public onChangeYear(year: any) {
        if (!year) return;
        this.store.dispatch([
            new GetTimeEntriesByActivities({ year: Number(year) }),
            new GetTimeEntriesByTypologies({ year: Number(year) }),
        ]);
    }
}
